import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React from 'react';
import { bind } from 'bind-event-listener';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import { NodeSelection } from '@atlaskit/editor-prosemirror/state';
import { findParentNodeOfType, findSelectedNodeOfType } from '@atlaskit/editor-prosemirror/utils';
import { createComponentRegistry } from '@atlaskit/editor-toolbar-model';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { getSelectionToolbarOpenExperiencePlugin } from './pm-plugins/experiences/selection-toolbar-open-experience';
import { editorToolbarPluginKey } from './pm-plugins/plugin-key';
import { DEFAULT_POPUP_SELECTORS } from './ui/consts';
import { SelectionToolbar, SelectionToolbarWithErrorBoundary } from './ui/SelectionToolbar';
import { getToolbarComponents } from './ui/toolbar-components';
import { isEventInContainer } from './ui/utils/toolbar';
function getSelectedNode(editorState) {
  var selection = editorState.selection;
  if (selection instanceof NodeSelection) {
    return {
      node: selection.node,
      pos: selection.from,
      nodeType: selection.node.type.name,
      marks: selection.node.marks.map(function (mark) {
        return "".concat(mark.type.name, ":").concat(JSON.stringify(mark.attrs));
      })
    };
  }
  var nodes = editorState.schema.nodes;
  var selectedNode = findSelectedNodeOfType([nodes.paragraph, nodes.heading, nodes.blockquote, nodes.panel, nodes.codeBlock])(selection);
  if (selectedNode) {
    return {
      node: selectedNode.node,
      pos: selectedNode.pos,
      nodeType: selectedNode.node.type.name,
      marks: selectedNode.node.marks.map(function (mark) {
        return "".concat(mark.type.name, ":").concat(JSON.stringify(mark.attrs));
      })
    };
  }
  var parentNode = findParentNodeOfType([nodes.paragraph, nodes.heading, nodes.blockquote, nodes.panel, nodes.listItem, nodes.taskItem])(selection);
  if (parentNode) {
    return {
      node: parentNode.node,
      pos: parentNode.pos,
      nodeType: parentNode.node.type.name,
      marks: parentNode.node.marks.map(function (mark) {
        return "".concat(mark.type.name, ":").concat(JSON.stringify(mark.attrs));
      })
    };
  }
  var $pos = selection.$from;
  return {
    node: $pos.parent,
    pos: $pos.pos,
    nodeType: $pos.parent.type.name,
    marks: $pos.marks().map(function (mark) {
      return "".concat(mark.type.name, ":").concat(JSON.stringify(mark.attrs));
    })
  };
}
export var toolbarPlugin = function toolbarPlugin(_ref) {
  var api = _ref.api,
    _ref$config = _ref.config,
    config = _ref$config === void 0 ? {
      disableSelectionToolbar: false,
      disableSelectionToolbarWhenPinned: false
    } : _ref$config;
  var refs = {};
  var disableSelectionToolbar = config.disableSelectionToolbar,
    disableSelectionToolbarWhenPinned = config.disableSelectionToolbarWhenPinned,
    _config$contextualFor = config.contextualFormattingEnabled,
    contextualFormattingEnabled = _config$contextualFor === void 0 ? 'always-pinned' : _config$contextualFor;
  var registry = createComponentRegistry();
  registry.register(getToolbarComponents(contextualFormattingEnabled, api, disableSelectionToolbar));
  return {
    name: 'toolbar',
    actions: {
      registerComponents: function registerComponents(toolbarComponents) {
        var replaceItems = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
        if (replaceItems) {
          registry.safeRegister(toolbarComponents);
        } else {
          registry.register(toolbarComponents);
        }
      },
      getComponents: function getComponents() {
        return registry.components;
      },
      contextualFormattingMode: function contextualFormattingMode() {
        return contextualFormattingEnabled;
      }
    },
    getSharedState: function getSharedState(editorState) {
      if (!editorState) {
        return undefined;
      }
      return editorToolbarPluginKey.getState(editorState);
    },
    pmPlugins: function pmPlugins() {
      return [{
        name: 'editor-toolbar-selection',
        plugin: function plugin() {
          return new SafePlugin({
            key: editorToolbarPluginKey,
            state: {
              init: function init(_, editorState) {
                return {
                  shouldShowToolbar: false,
                  selectedNode: getSelectedNode(editorState)
                };
              },
              apply: function apply(tr, pluginState, _, newState) {
                var meta = tr.getMeta(editorToolbarPluginKey);
                var newPluginState = _objectSpread({}, pluginState);
                if (expValEquals('platform_editor_toolbar_aifc_patch_5', 'isEnabled', true)) {
                  var shouldUpdateNode = tr.docChanged || tr.selectionSet;
                  if (shouldUpdateNode) {
                    var newSelectedNode = getSelectedNode(newState);
                    var oldNode = pluginState.selectedNode;
                    var hasNodeChanged = !oldNode || !newSelectedNode || oldNode.nodeType !== newSelectedNode.nodeType || oldNode.pos !== newSelectedNode.pos || JSON.stringify(oldNode.marks) !== JSON.stringify(newSelectedNode.marks);
                    if (hasNodeChanged) {
                      newPluginState.selectedNode = newSelectedNode;
                    }
                  }
                }
                if (meta) {
                  newPluginState = _objectSpread(_objectSpread({}, newPluginState), meta);
                }
                return newPluginState;
              }
            },
            view: function view(_view) {
              var unbind = bind(_view.root, {
                type: 'mouseup',
                listener: function listener(ev) {
                  var _api$editorViewMode;
                  var event = ev;
                  var isInToolbar = isEventInContainer(event, DEFAULT_POPUP_SELECTORS.toolbarContainer);
                  var isInPortal = isEventInContainer(event, DEFAULT_POPUP_SELECTORS.portal);

                  // We only want to set selectionStable to true if the editor has focus
                  // to prevent the toolbar from showing when the editor is blurred
                  // due to a click outside the editor.
                  var editorViewModePlugin = api === null || api === void 0 || (_api$editorViewMode = api.editorViewMode) === null || _api$editorViewMode === void 0 ? void 0 : _api$editorViewMode.sharedState.currentState();
                  var isViewModeEnabled = (editorViewModePlugin === null || editorViewModePlugin === void 0 ? void 0 : editorViewModePlugin.mode) === 'view';
                  _view.dispatch(_view.state.tr.setMeta(editorToolbarPluginKey, {
                    shouldShowToolbar: !isViewModeEnabled ? _view.hasFocus() || isInToolbar || isInPortal : true
                  }));
                }
              });
              var unbindEditorViewFocus = bind(_view.dom, {
                type: 'focus',
                listener: function listener() {
                  _view.dispatch(_view.state.tr.setMeta(editorToolbarPluginKey, {
                    shouldShowToolbar: true
                  }));
                }
              });
              return {
                destroy: function destroy() {
                  unbind();
                  unbindEditorViewFocus();
                }
              };
            },
            props: {
              handleDOMEvents: {
                mousedown: function mousedown(view) {
                  view.dispatch(view.state.tr.setMeta(editorToolbarPluginKey, {
                    shouldShowToolbar: false
                  }));
                  return false;
                }
              }
            }
          });
        }
      }].concat(_toConsumableArray(!disableSelectionToolbar && expValEquals('platform_editor_experience_tracking', 'isEnabled', true) ? [{
        name: 'selectionToolbarOpenExperience',
        plugin: function plugin() {
          return getSelectionToolbarOpenExperiencePlugin({
            refs: refs
          });
        }
      }] : []));
    },
    contentComponent: !disableSelectionToolbar ? function (_ref2) {
      var editorView = _ref2.editorView,
        popupsMountPoint = _ref2.popupsMountPoint;
      refs.popupsMountPoint = popupsMountPoint || undefined;
      if (!editorView) {
        return null;
      }
      if (fg('platform_editor_toolbar_aifc_patch_7')) {
        return /*#__PURE__*/React.createElement(SelectionToolbarWithErrorBoundary, {
          api: api,
          editorView: editorView,
          mountPoint: popupsMountPoint,
          disableSelectionToolbarWhenPinned: disableSelectionToolbarWhenPinned !== null && disableSelectionToolbarWhenPinned !== void 0 ? disableSelectionToolbarWhenPinned : false
        });
      }
      return /*#__PURE__*/React.createElement(SelectionToolbar, {
        api: api,
        editorView: editorView,
        mountPoint: popupsMountPoint,
        disableSelectionToolbarWhenPinned: disableSelectionToolbarWhenPinned !== null && disableSelectionToolbarWhenPinned !== void 0 ? disableSelectionToolbarWhenPinned : false
      });
    } : undefined
  };
};